import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

export default function InvitePage() {
  const { token }    = useParams<{ token: string }>();
  const navigate     = useNavigate();
  const { login }    = useAuth();

  const [email, setEmail]         = useState("");
  const [role, setRole]           = useState("");
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState("");
  const [invalid, setInvalid]     = useState(false);

  // Validate the invite token on mount
  useEffect(() => {
    if (!token) { setInvalid(true); setLoading(false); return; }
    fetch(`${API_BASE}/api/invite/${token}`)
      .then(async res => {
        if (!res.ok) { setInvalid(true); return; }
        const data = await res.json();
        setEmail(data.email);
        setRole(data.role);
      })
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match"); return; }
    if (password.length < 8)  { setError("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/invite/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Failed to accept invite");
      }
      // Auto-login after accepting
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err: any) {
      setError(err.message ?? "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    width: 380, background: "var(--surface)",
    border: "1px solid var(--border2)", borderRadius: "var(--r2)",
    padding: 32, boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex",
      alignItems: "center", justifyContent: "center",
      background: "var(--surface2)",
    }}>
      <div style={cardStyle}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <div style={{ width: 32, height: 32, background: "var(--ink)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 20, height: 20, background: "var(--teal)", borderRadius: 4 }} />
          </div>
          <div>
            <div style={{ fontWeight: 500, fontSize: 15, letterSpacing: "-0.3px" }}>FinBackup</div>
            <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)" }}>Financial Data Protection</div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", color: "var(--text3)", padding: "24px 0" }}>Validating invite…</div>
        ) : invalid ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>⚠</div>
            <p style={{ fontWeight: 500, color: "var(--text2)", marginBottom: 8 }}>Invalid or expired invite</p>
            <p style={{ fontSize: 13, color: "var(--text3)" }}>
              This invite link is no longer valid. Ask your admin to send a new one.
            </p>
          </div>
        ) : (
          <>
            <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 4, letterSpacing: "-0.3px" }}>
              Set up your account
            </h2>
            <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 20 }}>
              You've been invited as <strong style={{ color: "var(--text2)" }}>{email}</strong> with <strong style={{ color: "var(--text2)" }}>{role}</strong> access.
            </p>

            {error && (
              <div style={{
                background: "var(--red-bg)", color: "#791F1F", fontSize: 13,
                padding: "10px 14px", borderRadius: "var(--r)", marginBottom: 16,
              }}>{error}</div>
            )}

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border2)", borderRadius: "var(--r)", fontSize: 14, fontFamily: "var(--sans)", outline: "none", background: "var(--surface)" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Confirm password
                </label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  placeholder="Repeat your password"
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--border2)", borderRadius: "var(--r)", fontSize: 14, fontFamily: "var(--sans)", outline: "none", background: "var(--surface)" }}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
                style={{ marginTop: 4, width: "100%", padding: 10 }}
              >
                {submitting ? "Setting up…" : "Create account & sign in"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
